import type { ProductionItem } from '@qlk/shared';
import { PRODUCTION_TOOL_LABEL } from '@qlk/shared';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Music2, PencilLine } from 'lucide-react';

import { Avatar, StatusBadge } from '../../../ui/index.js';

/** `#4F46E5` → `rgba(79,70,229,0.12)` để làm nền chip kênh. */
export function nenNhat(mau: string, doDam = 0.12): string {
  const hex = mau.replace('#', '');
  const day = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
  const r = Number.parseInt(day.slice(0, 2), 16);
  const g = Number.parseInt(day.slice(2, 4), 16);
  const b = Number.parseInt(day.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return 'var(--color-neutral-nen)';
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(doDam)})`;
}

export function ngayGon(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const ngay = String(d.getDate()).padStart(2, '0');
  const thang = String(d.getMonth() + 1).padStart(2, '0');
  return `${ngay}/${thang}`;
}

export interface TheVideoProps {
  item: ProductionItem;
  onMo: (id: string) => void;
  /**
   * Ngày đăng thật lấy từ lịch của module 06 (ISO), `null` khi thẻ chưa lên lịch
   * hoặc người dùng không có quyền xem lịch.
   */
  ngayDang?: string | null;
  /** Hiện bản tĩnh khi đang kéo (DragOverlay). */
  tinh?: boolean;
}

export function TheVideo({ item, onMo, ngayDang = null, tinh = false }: TheVideoProps) {
  const sortable = useSortable({ id: item.id, data: { stage: item.stage }, disabled: tinh });

  const style = tinh
    ? undefined
    : {
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1,
      };

  const congCu = item.tool ? (PRODUCTION_TOOL_LABEL[item.tool] ?? item.tool) : null;

  return (
    <article
      ref={tinh ? undefined : sortable.setNodeRef}
      style={style}
      {...(tinh ? {} : sortable.attributes)}
      {...(tinh ? {} : sortable.listeners)}
      className="cursor-grab rounded-xl border border-vien bg-be-mat p-3 shadow-sm active:cursor-grabbing"
    >
      <button
        type="button"
        onClick={() => {
          onMo(item.id);
        }}
        className="block w-full text-left"
      >
        <span
          className="inline-block rounded-full px-2 py-0.5 text-phu font-semibold"
          style={{ backgroundColor: nenNhat(item.channelColor), color: item.channelColor }}
        >
          {item.channelName}
        </span>

        <h3 className="mt-2 text-co-ban leading-snug font-semibold text-chu">{item.title}</h3>

        <dl className="mt-2 space-y-1 text-phu text-chu-phu">
          {congCu && (
            <div className="flex items-center gap-1.5">
              <PencilLine size={14} aria-hidden="true" className="shrink-0 text-chu-mo" />
              <dt className="sr-only">Công cụ</dt>
              <dd>{congCu}</dd>
            </div>
          )}
          <div className="flex items-start gap-1.5">
            <Music2 size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-chu-mo" />
            <dt className="sr-only">Nhạc</dt>
            <dd>
              {item.trackIds.length === 0
                ? 'Chưa chọn nhạc'
                : `${String(item.trackIds.length)} bài đã gán`}
            </dd>
          </div>
        </dl>
      </button>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-vien pt-2.5">
        <span className="flex min-w-0 items-center gap-2">
          <Avatar name={item.assigneeName ?? '?'} color={item.channelColor} size={24} />
          <span className="truncate text-phu text-chu-phu">{item.assigneeName ?? 'Chưa giao'}</span>
        </span>

        {/*
          Thẻ đã có lịch thì hiện **ngày đăng** (khớp lịch), không hiện hạn làm
          xong và không gắn nhãn trễ: `dueDate` đã qua không còn ý nghĩa khi
          video đã lên lịch đăng.
        */}
        {ngayDang ? (
          <span className="so-lieu text-phu text-chu-phu">Đăng {ngayGon(ngayDang)}</span>
        ) : item.lateDays > 0 ? (
          <StatusBadge tone="bad" cham={false}>
            Trễ {item.lateDays} ngày
          </StatusBadge>
        ) : (
          <span className="so-lieu text-phu text-chu-mo">{ngayGon(item.dueDate)}</span>
        )}
      </div>
    </article>
  );
}
